import { useState } from "react";
import { GripVertical, Clock, Hand } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CharacterCard } from "@/components/CharacterCard";

export const FavoritesGrid = ({ favorites, onChange, folders, assign, onAssign, reorderable = true }) => {
  const { refreshUser } = useAuth();
  const [sortMode, setSortMode] = useState("manual");
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);

  const canDrag = sortMode === "manual" && reorderable;
  const manual = sortMode === "manual";
  const displayed = manual ? favorites : [...favorites].slice().reverse();

  const persist = async (newList) => {
    onChange?.(newList);
    try {
      await api.put("/favorites/order", { order: newList.map((c) => c.id) });
      await refreshUser();
    } catch (_) {
      toast.error("Reihenfolge konnte nicht gespeichert werden");
    }
  };

  const handleDrop = (dropIdx) => {
    if (dragIdx === null || dragIdx === dropIdx) {
      setDragIdx(null);
      setOverIdx(null);
      return;
    }
    const list = [...favorites];
    const [moved] = list.splice(dragIdx, 1);
    list.splice(dropIdx, 0, moved);
    setDragIdx(null);
    setOverIdx(null);
    persist(list);
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-5" data-testid="favorites-sort">
        <button
          data-testid="fav-sort-manual"
          onClick={() => setSortMode("manual")}
          className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all ${
            manual
              ? "bg-fuchsia-500/20 text-fuchsia-200 border border-fuchsia-400/40"
              : "bg-white/5 text-slate-400 hover:text-white hover:bg-white/10 border border-white/5"
          }`}
        >
          <Hand className="w-3.5 h-3.5" /> Manuell
        </button>
        <button
          data-testid="fav-sort-recent"
          onClick={() => setSortMode("recent")}
          className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all ${
            !manual
              ? "bg-fuchsia-500/20 text-fuchsia-200 border border-fuchsia-400/40"
              : "bg-white/5 text-slate-400 hover:text-white hover:bg-white/10 border border-white/5"
          }`}
        >
          <Clock className="w-3.5 h-3.5" /> Zuletzt hinzugefügt
        </button>
        {canDrag && (
          <span className="text-xs text-slate-500 hidden sm:inline">Zum Ordnen ziehen & ablegen</span>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-5">
        {displayed.map((c, i) => (
          <div
            key={c.id}
            data-testid={`fav-item-${i}`}
            draggable={canDrag}
            onDragStart={() => canDrag && setDragIdx(i)}
            onDragOver={(e) => {
              if (!canDrag) return;
              e.preventDefault();
              setOverIdx(i);
            }}
            onDrop={() => canDrag && handleDrop(i)}
            onDragEnd={() => {
              setDragIdx(null);
              setOverIdx(null);
            }}
            className={`relative transition-all ${canDrag ? "cursor-grab active:cursor-grabbing" : ""} ${
              overIdx === i && dragIdx !== null && dragIdx !== i
                ? "ring-2 ring-fuchsia-400 rounded-2xl scale-[1.02]"
                : ""
            } ${dragIdx === i ? "opacity-40" : ""}`}
          >
            {canDrag && (
              <div className="absolute top-2 left-1/2 -translate-x-1/2 z-20 px-2 h-6 flex items-center justify-center rounded-full bg-black/60 backdrop-blur-md border border-white/10 text-slate-200 pointer-events-none">
                <GripVertical className="w-4 h-4" />
              </div>
            )}
            <CharacterCard character={c} index={i} />
            {folders && folders.length > 0 && (
              <div className="mt-2" onClick={(e) => e.stopPropagation()}>
                <select
                  data-testid={`fav-folder-select-${c.id}`}
                  value={assign?.[c.id] || ""}
                  onPointerDown={(e) => e.stopPropagation()}
                  onChange={(e) => onAssign?.(c.id, e.target.value || null)}
                  className="w-full bg-[#0d0e15] border border-white/10 rounded-lg text-xs text-slate-300 px-2 py-1.5 outline-none focus:border-fuchsia-500/50"
                >
                  <option value="">Kein Ordner</option>
                  {folders.map((f) => (
                    <option key={f.id} value={f.id}>{f.name}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
